import React from 'react';
import { Box, Container, Typography, Grid, Divider } from '@mui/material';
import useSEO from '../hooks/useSEO';
import Hero from '../Components/sections/Hero';
import InfoSection from '../Components/sections/InfoSection';
import BenefitCard from '../Components/sections/BenefitCard';
import CTAButton from '../Components/buttons/CTAButton';
import Testimonial from '../Components/sections/Testimonial';
import ProcessFlow from '../Components/sections/ProcessFlow';

// Icons
import AgricultureIcon from '@mui/icons-material/Agriculture';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import VerifiedIcon from '@mui/icons-material/Verified';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

// Images
import BannerBackground from '../Assets/home-banner-background.png';
import AboutImage from '../Assets/about-background-image.png';

const ComponentsDemo = () => {
  const SEO = useSEO();

  // Sample data for ProcessFlow
  const processSteps = [
    {
      icon: AgricultureIcon,
      title: 'Plantio',
      description: 'Cultivo agroecológico sem agrotóxicos'
    },
    {
      icon: VerifiedIcon,
      title: 'Colheita',
      description: 'Colheita no ponto ideal de maturação'
    },
    {
      icon: LocalShippingIcon,
      title: 'Entrega',
      description: 'Entrega rápida e fresca na sua casa'
    }
  ];

  return (
    <Box>
      {SEO}

      {/* Page Header */}
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Typography
          variant="h1"
          sx={{
            fontFamily: '"Reem Kufi", sans-serif',
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: 700,
            color: '#1B422E',
            textAlign: 'center',
            mb: 2
          }}
        >
          Componentes Reutilizáveis
        </Typography>
        <Typography
          sx={{
            fontFamily: '"Reem Kufi", sans-serif',
            fontSize: '1.1rem',
            color: '#505050',
            textAlign: 'center',
            mb: 4
          }}
        >
          Demonstração de todos os componentes criados na Fase 2
        </Typography>
      </Container>

      <Divider sx={{ mb: 5 }} />

      {/* 1. Hero Component */}
      <Box sx={{ mb: 5 }}>
        <Container maxWidth="lg" sx={{ mb: 3 }}>
          <Typography variant="h2" sx={{ fontFamily: '"Reem Kufi"', fontSize: '2rem', color: '#1B422E', mb: 1 }}>
            1. Hero Component
          </Typography>
          <Typography sx={{ color: '#505050', mb: 2 }}>
            Banner de topo com imagem de fundo, título, subtítulo e CTA opcional.
          </Typography>
        </Container>

        <Hero
          title="Produtos Agroecológicos Frescos"
          subtitle="Do nosso sítio direto para sua mesa, com qualidade e sustentabilidade"
          backgroundImage={BannerBackground}
          ctaText="Faça seu Pedido"
          ctaType="whatsapp"
          ctaVariant="whatsapp"
          ctaMessage="Olá! Gostaria de fazer um pedido."
          height="400px"
        />
      </Box>

      <Divider sx={{ my: 5 }} />

      {/* 2. InfoSection Component */}
      <Box sx={{ mb: 5 }}>
        <Container maxWidth="lg" sx={{ mb: 3 }}>
          <Typography variant="h2" sx={{ fontFamily: '"Reem Kufi"', fontSize: '2rem', color: '#1B422E', mb: 1 }}>
            2. InfoSection Component
          </Typography>
          <Typography sx={{ color: '#505050', mb: 2 }}>
            Seção de informação com título, descrição e imagem opcional (esquerda/direita).
          </Typography>
        </Container>

        <InfoSection
          title="Sobre a Verde Musgo Natural"
          description={[
            'Somos um sítio agroecológico certificado pelo governo estadual de São Paulo, dedicado à produção sustentável de alimentos.',
            'Nossos produtos são cultivados sem agrotóxicos, respeitando os ciclos naturais e a biodiversidade.'
          ]}
          image={AboutImage}
          imagePosition="right"
          icon={AgricultureIcon}
          backgroundColor="#f9f9f9"
        />
      </Box>

      <Divider sx={{ my: 5 }} />

      {/* 3. BenefitCard Component */}
      <Box sx={{ mb: 5 }}>
        <Container maxWidth="lg">
          <Typography variant="h2" sx={{ fontFamily: '"Reem Kufi"', fontSize: '2rem', color: '#1B422E', mb: 1 }}>
            3. BenefitCard Component
          </Typography>
          <Typography sx={{ color: '#505050', mb: 3 }}>
            Cards com ícone, título e descrição para destacar benefícios.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <BenefitCard
                icon={AgricultureIcon}
                title="100% Agroecológico"
                description="Produtos livres de agrotóxicos e cultivados de forma sustentável"
                iconColor="#1B422E"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <BenefitCard
                icon={VerifiedIcon}
                title="Certificado"
                description="Certificação oficial do governo estadual de São Paulo"
                iconColor="#1B422E"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <BenefitCard
                icon={LocalShippingIcon}
                title="Entrega Rápida"
                description="Produtos frescos entregues diretamente na sua casa"
                iconColor="#1B422E"
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Divider sx={{ my: 5 }} />

      {/* 4. CTAButton Component */}
      <Box sx={{ mb: 5 }}>
        <Container maxWidth="lg">
          <Typography variant="h2" sx={{ fontFamily: '"Reem Kufi"', fontSize: '2rem', color: '#1B422E', mb: 1 }}>
            4. CTAButton Component
          </Typography>
          <Typography sx={{ color: '#505050', mb: 3 }}>
            Botões de Call-to-Action com diferentes variantes e tipos.
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <CTAButton
              text="WhatsApp"
              type="whatsapp"
              variant="whatsapp"
              message="Olá! Tenho interesse nos produtos."
            />
            <CTAButton
              text="Botão Primário"
              type="link"
              href="/loja-online"
              variant="primary"
            />
            <CTAButton
              text="Botão Secundário"
              type="link"
              href="/sobre"
              variant="secondary"
            />
            <CTAButton
              text="Botão Outlined"
              type="link"
              href="/contato"
              variant="outlined"
            />
            <CTAButton
              text="Loja Externa"
              type="external"
              href="https://loja.verdemusgonatural.com.br"
              variant="primary"
              icon={ShoppingCartIcon}
            />
          </Box>
        </Container>
      </Box>

      <Divider sx={{ my: 5 }} />

      {/* 5. ProcessFlow Component */}
      <Box sx={{ mb: 5 }}>
        <Container maxWidth="lg" sx={{ mb: 3 }}>
          <Typography variant="h2" sx={{ fontFamily: '"Reem Kufi"', fontSize: '2rem', color: '#1B422E', mb: 1 }}>
            5. ProcessFlow Component
          </Typography>
          <Typography sx={{ color: '#505050', mb: 2 }}>
            Fluxo de processo com steps numerados e orientação horizontal/vertical.
          </Typography>
        </Container>

        <ProcessFlow
          title="Nosso Processo"
          subtitle="Do Campo à Sua Mesa"
          steps={processSteps}
          orientation="horizontal"
          numbered={true}
          backgroundColor="#f9f9f9"
        />
      </Box>

      <Divider sx={{ my: 5 }} />

      {/* 6. Testimonial Component */}
      <Box sx={{ mb: 5 }}>
        <Container maxWidth="lg" sx={{ mb: 3 }}>
          <Typography variant="h2" sx={{ fontFamily: '"Reem Kufi"', fontSize: '2rem', color: '#1B422E', mb: 1 }}>
            6. Testimonial Component
          </Typography>
          <Typography sx={{ color: '#505050', mb: 2 }}>
            Depoimentos de clientes em formato de cards com avatar e avaliação.
          </Typography>
        </Container>

        <Testimonial />
      </Box>
    </Box>
  );
};

export default ComponentsDemo;
